import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Bot, Plus } from 'lucide-react'
import { areaLabel, copy, findingLabel } from '../../copy/investor-copy'
import { formatShortDate } from '../following/dates'
import { readAreaRows } from './assessment-rows'
import { PrivateTag, RelCard } from './rel-card'
import type { CardActions, CardVariant, Relationship } from './types'
import { VerdictPill } from './verdict-pill'

const LABEL = 'text-xs font-medium uppercase tracking-wider text-muted-foreground'

/** The line under a heading: "Saved by Claude · 12 Oct 2026 · version 1", or the pass wording. */
export function savedByLine(assessment: NonNullable<Relationship['assessment']>): string {
  const vars = {
    client: assessment.savedBy,
    date: formatShortDate(assessment.savedAt),
  }
  return assessment.source === 'pass'
    ? copy('ai.savedByWithPass', vars)
    : copy('ai.savedBy', { ...vars, version: assessment.version })
}

/** The investor's AI's read of a startup, short: the main risk, each area, what would change it. */
export function AssessmentCard({
  data,
  actions,
}: {
  data: Relationship
  variant?: CardVariant
  actions: Pick<CardActions, 'onFollow' | 'onOpenAssessment'>
}) {
  const assessment = data.assessment
  if (!assessment) return null
  const rows = readAreaRows(assessment.dimensions)
  const startup = data.startup.name

  return (
    <RelCard title={copy('ai.title')} right={<PrivateTag label={copy('ai.private')} />}>
      <p className="mt-1 text-xs text-muted-foreground">{savedByLine(assessment)}</p>
      {assessment.movedAgainstAt && (
        <p className="mt-0.5 text-xs text-muted-foreground">
          {copy('ai.moved', { startup, date: formatShortDate(assessment.movedAgainstAt) })}
        </p>
      )}
      {(assessment.mainRisk || assessment.mainRiskText) && (
        <>
          <p className={`mt-3 ${LABEL}`}>{copy('ai.mainRisk')}</p>
          <p className="mt-1 text-sm leading-relaxed text-foreground/90">
            {assessment.mainRisk && (
              <span className="font-medium text-foreground">{areaLabel(assessment.mainRisk)}.</span>
            )}
            {assessment.mainRiskText && ` ${assessment.mainRiskText}`}
          </p>
        </>
      )}
      {rows.length > 0 && (
        <>
          <p className={`mt-3 ${LABEL}`}>{copy('ai.byArea')}</p>
          <ul className="mt-0.5">
            {rows.map(({ area, read }) => (
              <li key={area} className="flex items-center justify-between gap-3 py-1.5">
                <span className="min-w-0 text-sm text-foreground">
                  {areaLabel(area)}
                  {read.kind && (
                    <span className="text-muted-foreground">
                      {' · '}
                      {findingLabel(area, read.kind)}
                    </span>
                  )}
                </span>
                <span className="shrink-0">
                  <VerdictPill verdict={read.verdict} />
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {assessment.wouldChangeMind && (
        <>
          <p className={`mt-3 ${LABEL}`}>{copy('ai.wouldChange')}</p>
          <p className="mt-1 text-sm text-foreground/90">{assessment.wouldChangeMind}</p>
        </>
      )}
      {data.state === 'NONE' && assessment.ringOn && (
        <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-3">
          <p className="min-w-0 text-sm text-foreground/90">
            {copy('ai.followRow', { startup, ringOn: assessment.ringOn })}
          </p>
          <SecondaryButton
            size="sm"
            icon={<Plus />}
            onClick={(e) => actions.onFollow(assessment.ringOn, e.currentTarget)}
          >
            {copy('ai.follow')}
          </SecondaryButton>
        </div>
      )}
      <div className="mt-3 border-t border-border pt-3">
        <SecondaryButton size="sm" icon={<Bot />} onClick={actions.onOpenAssessment}>
          {copy('ai.readFull')}
        </SecondaryButton>
      </div>
    </RelCard>
  )
}
